import { Color } from '../design-system'
import { FontWeight, TextTransform } from '../util'

interface SubtleButtonColors {
  icon: Color
  text: Color
  background: Color
}

export interface SubtleButtonTheme {
  default: SubtleButtonColors
  hover: SubtleButtonColors
  active: SubtleButtonColors
  borderColor: Color
  loaderForeground: Color
  loaderBackground: Color
  fontWeight?: FontWeight
  textTransform?: TextTransform
  buttonWidth?: string
}

export const SUBTLE_BUTTON_THEME_DISABLED: SubtleButtonTheme = {
  default: {
    icon: Color.Grey30,
    text: Color.Grey30,
    background: Color.White,
  },
  hover: {
    icon: Color.Grey30,
    text: Color.Grey30,
    background: Color.White,
  },
  active: {
    icon: Color.Grey30,
    text: Color.Grey30,
    background: Color.White,
  },
  borderColor: Color.Grey20,
  loaderForeground: Color.White,
  loaderBackground: Color.White,
}

const defaultTheme = {
  loaderBackground: Color.Grey20,
  loaderForeground: Color.Grey40,
  borderColor: Color.Grey20,
  fontWeight: FontWeight.Regular,
  textTransform: TextTransform.None,
  buttonWidth: 'fit-content',
}

export const SUBTLE_BUTTON_THEMES = {
  default: {
    ...defaultTheme,
    default: {
      icon: Color.Grey40,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Grey10,
    },
    active: {
      icon: Color.Grey70,
      text: Color.Grey70,
      background: Color.Grey20,
    },
  },
  hearing: {
    ...defaultTheme,
    default: {
      icon: Color.Mint50,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Mint50,
      text: Color.Grey50,
      background: Color.Mint10,
    },
    active: {
      icon: Color.Mint50,
      text: Color.Grey50,
      background: Color.Mint20,
    },
    loaderForeground: Color.Mint50,
    loaderBackground: Color.Mint20,
  },
  autoGeneratedHearing: {
    ...defaultTheme,
    default: {
      icon: Color.Watermelon50,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Watermelon60,
      text: Color.Grey50,
      background: Color.Watermelon10,
    },
    active: {
      icon: Color.Watermelon60,
      text: Color.Grey50,
      background: Color.Watermelon20,
    },
    loaderForeground: Color.Watermelon50,
    loaderBackground: Color.Watermelon20,
  },
  sealing: {
    ...defaultTheme,
    default: {
      icon: Color.Grey40,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Grey40,
      text: Color.Grey50,
      background: Color.Grey20,
    },
    active: {
      icon: Color.Grey70,
      text: Color.Grey70,
      background: Color.Grey30,
    },
    loaderForeground: Color.Grey50,
    loaderBackground: Color.Grey30,
  },
  session: {
    ...defaultTheme,
    default: {
      icon: Color.Blueberry50,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Blueberry50,
      text: Color.Grey50,
      background: Color.Blueberry10,
    },
    active: {
      icon: Color.Blueberry50,
      text: Color.Grey50,
      background: Color.Blueberry20,
    },
    loaderForeground: Color.Blueberry50,
    loaderBackground: Color.Blueberry20,
  },
  startSession: {
    ...defaultTheme,
    default: {
      icon: Color.White,
      text: Color.White,
      background: Color.Primary50,
    },
    hover: {
      icon: Color.White,
      text: Color.White,
      background: Color.Primary60,
    },
    active: {
      icon: Color.White,
      text: Color.White,
      background: Color.Primary50,
    },

    borderColor: Color.Primary70,
    loaderForeground: Color.Primary60,
    loaderBackground: Color.Primary80,
  },
  destructiveSecondary: {
    ...defaultTheme,
    default: {
      icon: Color.Error50,
      text: Color.Error50,
      background: Color.White,
    },
    hover: {
      icon: Color.Error50,
      text: Color.Error50,
      background: Color.Brand10,
    },
    active: {
      icon: Color.White,
      text: Color.White,
      background: Color.Brand60,
    },
    borderColor: Color.Error50,
    loaderForeground: Color.Error50,
    loaderBackground: Color.Brand10,
  },
  startSessionWithDepartment: {
    default: {
      icon: Color.White,
      text: Color.White,
      background: Color.Primary50,
    },
    hover: {
      icon: Color.White,
      text: Color.White,
      background: Color.Primary60,
    },
    active: {
      icon: Color.White,
      text: Color.White,
      background: Color.Primary50,
    },

    borderColor: Color.Primary70,
    loaderForeground: Color.Primary60,
    loaderBackground: Color.Primary80,
    fontWeight: FontWeight.Bold,
    textTransform: TextTransform.Uppercase,
    buttonWidth: '13rem',
  },
  start: {
    ...defaultTheme,
    default: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Grey10,
    },
    active: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Grey20,
    },
    loaderForeground: Color.Mint50,
    loaderBackground: Color.Mint20,
  },
  stop: {
    ...defaultTheme,
    default: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Grey10,
    },
    active: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Grey20,
    },
    loaderForeground: Color.Brand50,
    loaderBackground: Color.Brand20,
  },
  'mute-off': {
    ...defaultTheme,
    default: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Grey10,
    },
    active: {
      icon: Color.Grey70,
      text: Color.Grey70,
      background: Color.Grey20,
    },
  },
  'mute-on': {
    ...defaultTheme,
    default: {
      icon: Color.Grey70,
      text: Color.Grey70,
      background: Color.Grey20,
    },
    hover: {
      icon: Color.Grey70,
      text: Color.Grey70,
      background: Color.Grey30,
    },
    active: {
      icon: Color.Grey70,
      text: Color.Grey70,
      background: Color.Grey40,
    },
    borderColor: Color.Grey40,
  },
  'solo-off': {
    ...defaultTheme,
    default: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Primary10,
    },
    active: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Primary20,
    },
  },
  'solo-on': {
    ...defaultTheme,
    default: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Primary20,
    },
    hover: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Primary30,
    },
    active: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Primary40,
    },
    borderColor: Color.Primary50,
  },
  // TODO: manage-courtroom is also same as the default theme
  'manage-courtroom': {
    ...defaultTheme,
    default: {
      icon: Color.Grey40,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Grey50,
      text: Color.Grey50,
      background: Color.Grey10,
    },
    active: {
      icon: Color.Grey70,
      text: Color.Grey70,
      background: Color.Grey20,
    },
  },
  'segment-event': {
    ...defaultTheme,
    default: {
      icon: Color.Mandarin50,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Mandarin60,
      text: Color.Grey50,
      background: Color.Grey10,
    },
    active: {
      icon: Color.Mandarin70,
      text: Color.Grey70,
      background: Color.Grey20,
    },
    loaderForeground: Color.Mandarin50,
    loaderBackground: Color.Mandarin20,
  },
  'toggle-on': {
    ...defaultTheme,
    default: {
      icon: Color.Primary50,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Primary50,
      text: Color.Grey50,
      background: Color.Grey10,
    },
    active: {
      icon: Color.Primary50,
      text: Color.Grey50,
      background: Color.Grey20,
    },
  },
  'toggle-off': {
    ...defaultTheme,
    default: {
      icon: Color.Grey40,
      text: Color.Grey50,
      background: Color.White,
    },
    hover: {
      icon: Color.Grey40,
      text: Color.Grey50,
      background: Color.Grey10,
    },
    active: {
      icon: Color.Grey40,
      text: Color.Grey50,
      background: Color.Grey20,
    },
  },
} as const satisfies Record<string, SubtleButtonTheme>

export type SubtleButtonThemeName = keyof typeof SUBTLE_BUTTON_THEMES
